import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { User, Package, LogOut, Heart, MapPin, Plus, Trash2, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import api, { taka } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { STATUS_LABEL, STATUS_COLOR, DISTRICTS } from "@/lib/bd";
import { Modal, Field, inputCls } from "@/pages/admin/AdminUI";

export default function CustomerDashboard() {
  const { user, logout, loading } = useAuth();
  const { addItem } = useCart();
  const { ids: wishIds, toggle } = useWishlist();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "orders";
  const [orders, setOrders] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [addrOpen, setAddrOpen] = useState(false);
  const [addr, setAddr] = useState({ label: "বাসা", name: "", mobile: "", address: "", district: "", upazila: "" });

  useEffect(() => {
    if (!loading && !user) nav("/login");
    if (user) {
      api.get("/my/orders").then((r) => setOrders(r.data)).catch(() => {});
      api.get("/my/addresses").then((r) => setAddresses(r.data)).catch(() => {});
    }
  }, [user, loading]); // eslint-disable-line

  useEffect(() => {
    if (user) api.get("/my/wishlist").then((r) => setWishlist(r.data)).catch(() => {});
  }, [user, wishIds]); // eslint-disable-line

  if (!user) return null;
  const setTab = (t) => setParams({ tab: t });

  const saveAddr = async () => {
    if (!addr.name || !addr.mobile || !addr.address || !addr.district) return toast.error("সব তথ্য দিন");
    const { data } = await api.post("/my/addresses", addr);
    setAddresses((a) => [...a, data]); setAddrOpen(false);
    setAddr({ label: "বাসা", name: "", mobile: "", address: "", district: "", upazila: "" });
    toast.success("ঠিকানা সংরক্ষিত হয়েছে");
  };
  const delAddr = async (id) => { await api.delete(`/my/addresses/${id}`); setAddresses((a) => a.filter((x) => x.id !== id)); };
  const upazilas = DISTRICTS.find((d) => d.name === addr.district)?.upazilas || [];

  const tabs = [["orders", "আমার অর্ডার", Package], ["wishlist", "উইশলিস্ট", Heart], ["addresses", "ঠিকানা", MapPin]];

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
      <div className="bg-white rounded-2xl border border-stone-200 p-5 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-14 h-14 rounded-2xl bg-emerald-50 text-emerald-700 flex items-center justify-center"><User size={28} /></div>
          <div>
            <div className="font-bold text-[#1E3A2B] text-lg">{user.name}</div>
            <div className="text-sm text-stone-500">{user.email}</div>
          </div>
        </div>
        <button onClick={() => { logout(); nav("/"); }} className="flex items-center gap-1.5 text-red-600 border border-red-200 rounded-xl px-4 h-11 font-medium" data-testid="customer-logout">
          <LogOut size={16} /> লগআউট
        </button>
      </div>

      <div className="flex gap-2 mt-6 border-b border-stone-200">
        {tabs.map(([key, label, Icon]) => (
          <button key={key} onClick={() => setTab(key)} data-testid={`tab-${key}`}
            className={`flex items-center gap-1.5 px-4 py-3 font-medium border-b-2 -mb-px ${tab === key ? "border-[#CA8A04] text-[#1E3A2B]" : "border-transparent text-stone-500"}`}>
            <Icon size={16} /> {label} {key === "wishlist" && wishlist.length ? `(${wishlist.length})` : ""}
          </button>
        ))}
      </div>

      {tab === "orders" && (
        <div className="mt-5 space-y-3">
          {orders.length === 0 ? (
            <div className="bg-white rounded-2xl border border-stone-200 p-10 text-center text-stone-500">এখনো কোনো অর্ডার নেই<div className="mt-3"><Link to="/" className="text-emerald-700 font-semibold">কেনাকাটা শুরু করুন →</Link></div></div>
          ) : orders.map((o) => (
            <Link to={`/track?order=${o.order_no}`} key={o.id} className="block bg-white rounded-2xl border border-stone-200 p-4 hover:shadow-md transition-shadow" data-testid={`my-order-${o.order_no}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div><div className="font-bold text-[#1E3A2B]">{o.order_no}</div><div className="text-xs text-stone-500">{new Date(o.created_at).toLocaleDateString("bn-BD")} • {o.items.length} টি পণ্য</div></div>
                <div className="flex items-center gap-3"><span className="font-bold text-emerald-800">{taka(o.total)}</span><span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${STATUS_COLOR[o.status]}`}>{STATUS_LABEL[o.status]}</span></div>
              </div>
            </Link>
          ))}
        </div>
      )}

      {tab === "wishlist" && (
        <div className="mt-5">
          {wishlist.length === 0 ? (
            <div className="bg-white rounded-2xl border border-stone-200 p-10 text-center text-stone-500">উইশলিস্ট খালি<div className="mt-3"><Link to="/" className="text-emerald-700 font-semibold">পণ্য দেখুন →</Link></div></div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {wishlist.map((p) => (
                <div key={p.id} className="bg-white rounded-2xl border border-stone-200 p-3 flex gap-3 items-center" data-testid={`wish-item-${p.id}`}>
                  <Link to={`/product/${p.id}`}><img src={p.image} alt="" className="w-20 h-20 rounded-xl object-cover" /></Link>
                  <div className="flex-1 min-w-0">
                    <Link to={`/product/${p.id}`} className="font-semibold text-[#1C1917] truncate block">{p.name_bn}</Link>
                    <div className="text-emerald-800 font-bold">{taka(p.price)} <span className="text-xs text-stone-500 font-normal">/ {p.unit}</span></div>
                    <div className="mt-2 flex gap-2">
                      <button onClick={() => { addItem(p, 1); toast.success("কার্টে যোগ হয়েছে"); }} className="text-xs bg-[#1E3A2B] text-white px-3 py-1.5 rounded-lg flex items-center gap-1" data-testid={`wish-add-cart-${p.id}`}><ShoppingCart size={13} /> কার্ট</button>
                      <button onClick={async () => { await toggle(p.id); toast.success("সরানো হয়েছে"); }} className="text-xs text-red-500 px-2 py-1.5 rounded-lg flex items-center gap-1" data-testid={`wish-remove-${p.id}`}><Trash2 size={13} /> সরান</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "addresses" && (
        <div className="mt-5 space-y-3">
          <button onClick={() => setAddrOpen(true)} className="bg-[#1E3A2B] text-white h-11 px-4 rounded-xl flex items-center gap-2 font-medium" data-testid="add-address-btn"><Plus size={18} /> নতুন ঠিকানা</button>
          {addresses.length === 0 ? <div className="bg-white rounded-2xl border border-stone-200 p-10 text-center text-stone-500">কোনো সংরক্ষিত ঠিকানা নেই</div> : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {addresses.map((a) => (
                <div key={a.id} className="bg-white rounded-2xl border border-stone-200 p-4" data-testid={`address-${a.id}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full">{a.label}</span>
                    <button onClick={() => delAddr(a.id)} className="text-red-500" data-testid={`del-address-${a.id}`}><Trash2 size={15} /></button>
                  </div>
                  <div className="mt-2 font-semibold text-[#1C1917]">{a.name}</div>
                  <div className="text-sm text-stone-500">{a.mobile}</div>
                  <div className="text-sm text-stone-500">{a.address}, {a.upazila}, {a.district}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <Modal open={addrOpen} onClose={() => setAddrOpen(false)} title="নতুন ঠিকানা">
        <div className="space-y-3">
          <Field label="লেবেল"><input className={inputCls} value={addr.label} onChange={(e) => setAddr({ ...addr, label: e.target.value })} data-testid="addr-label" /></Field>
          <Field label="নাম"><input className={inputCls} value={addr.name} onChange={(e) => setAddr({ ...addr, name: e.target.value })} data-testid="addr-name" /></Field>
          <Field label="মোবাইল"><input className={inputCls} value={addr.mobile} onChange={(e) => setAddr({ ...addr, mobile: e.target.value })} data-testid="addr-mobile" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="জেলা"><select className={inputCls} value={addr.district} onChange={(e) => setAddr({ ...addr, district: e.target.value, upazila: "" })} data-testid="addr-district"><option value="">নির্বাচন</option>{DISTRICTS.map((d) => <option key={d.name}>{d.name}</option>)}</select></Field>
            <Field label="উপজেলা"><select className={inputCls} value={addr.upazila} onChange={(e) => setAddr({ ...addr, upazila: e.target.value })} data-testid="addr-upazila"><option value="">নির্বাচন</option>{upazilas.map((u) => <option key={u}>{u}</option>)}</select></Field>
          </div>
          <Field label="সম্পূর্ণ ঠিকানা"><textarea className={`${inputCls} h-20 py-2`} value={addr.address} onChange={(e) => setAddr({ ...addr, address: e.target.value })} data-testid="addr-address" /></Field>
          <button onClick={saveAddr} className="w-full bg-[#1E3A2B] text-white h-11 rounded-xl font-medium" data-testid="save-address-btn">সংরক্ষণ</button>
        </div>
      </Modal>
    </div>
  );
}
